// Copyright (c) Ping. All rights reserved.

import React, { useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
// Class-aware scheme (data-theme/.dark) — NEVER react-native's OS-level
// useColorScheme (renders the wrong palette on reload). See repo CLAUDE.md.
import { useColorScheme } from '../foundations/colorScheme';
import { MessageBubble } from '../chat/MessageBubble';
import type { MessageMenuAction } from '../chat/MessageActionMenu';
import { VoiceMessageBubble } from '../chat/VoiceMessageBubble';
import { ImageAttachmentCard } from '../chat/attachments/ImageAttachmentCard';
import { DocAttachmentCard } from '../chat/attachments/DocAttachmentCard';
import { LocationAttachmentCard } from '../chat/attachments/LocationAttachmentCard';
import { GameMessageCard } from '../chat/games/GameMessageCard';
import { PollView } from '../polls/PollView';
import { deriveVariantFromBody } from '../appEvent';
import { Avatar } from '../components/Avatar';
import type { Message } from '../sync/persistence';
import { threadsStrings } from './strings';
import type { ThreadReply } from './types';
import type { ThreadRowActions } from './ThreadOpenPanel';

export interface ThreadMessageRowProps {
  readonly reply: ThreadReply;
  /** Hide the avatar + author block when this row is part of a same-author group. */
  readonly grouped?: boolean;
  readonly onRetry?: ((reply: ThreadReply) => void) | undefined;
  /** Message-action handlers — drives the hover/long-press actions popover. */
  readonly actions?: ThreadRowActions | undefined;
  readonly testID?: string;
}

/**
 * Single reply row inside a thread. Reuses the wave-1 `MessageBubble`
 * primitive for the bubble itself; this wrapper adds the author /
 * avatar / timestamp / send-failed affordances that the bubble does not
 * own. Renders pure text content — never `dangerouslySetInnerHTML`.
 */
export const ThreadMessageRow = React.memo<ThreadMessageRowProps>(
  ({ reply, grouped = false, onRetry, actions, testID }) => {
    // Subscribe to the class-aware scheme so the row re-renders on theme
    // flips (the bubble + avatar read it internally too).
    useColorScheme();

    // Convert the thread reply into the shared `Message` shape consumed
    // by `MessageBubble`. The cast is intentional — `inReplyTo` does not
    // apply inside a thread and `mentions` is owned by the host
    // wherever it cares about mentions; the slice keeps the row body
    // simple to avoid drift.
    const message = useMemo<Message>(
      () => ({
        id: reply.id,
        conversationId: reply.threadId,
        senderId: reply.authorId,
        body: reply.body,
        hlc: '',
        contentHash: '',
        timestamp: reply.timestamp,
        isSelf: reply.isSelf,
      }),
      [reply],
    );

    const showHeader = !grouped;
    const showFailedHint = reply.status === 'failed';

    // Adapt the bubble's message-centric callbacks to the thread's
    // reply-centric handlers. `inThread` hides "Reply in Thread".
    const reactions = actions?.reactionsByReplyId?.[reply.id];
    const bubbleActionProps = {
      ...(actions?.onReact !== undefined
        ? { onReact: () => actions.onReact?.(reply) }
        : {}),
      ...(actions?.onToggleReaction !== undefined
        ? { onToggleReaction: (emoji: string) => actions.onToggleReaction?.(reply, emoji) }
        : {}),
      ...(actions?.onMenuAction !== undefined
        ? { onMenuAction: (action: MessageMenuAction) => actions.onMenuAction?.(action, reply) }
        : {}),
      ...(reactions !== undefined ? { reactions } : {}),
    };

    // Derive a media variant from the raw envelope so image / voice /
    // poll / file / location / game replies render as full tiles inside
    // the thread (not a plain text preview). Falls back to the text
    // bubble for plain text / undecodable bodies.
    const variant = useMemo(
      () =>
        reply.rawBody !== undefined && reply.rawBody.length > 0
          ? deriveVariantFromBody(reply.rawBody, { message })
          : undefined,
      [reply.rawBody, message],
    );

    let bodyNode: React.ReactNode;
    // Text bubbles self-align internally (rowSelf/rowOther); media tiles
    // need an explicit row wrapper to land self→right / other→left.
    let isTextBubble = false;
    if (variant?.kind === 'voice') {
      bodyNode = (
        <VoiceMessageBubble
          state={variant.state}
          durationMs={variant.durationMs}
          waveform={variant.waveform}
          progress={variant.progress}
          timestamp={new Date(reply.timestamp)}
          isSelf={reply.isSelf}
          hideMeta
          onTogglePlay={variant.onTogglePlay}
          onRetry={variant.onRetry}
          {...(variant.transcript !== undefined ? { transcript: variant.transcript } : {})}
        />
      );
    } else if (variant?.kind === 'image') {
      bodyNode = (
        <ImageAttachmentCard
          state={variant.state}
          aspectRatio={variant.aspectRatio}
          {...(variant.blurhash !== undefined ? { blurhash: variant.blurhash } : {})}
          {...(variant.imageUri !== undefined ? { imageUri: variant.imageUri } : {})}
          {...(variant.caption !== undefined ? { caption: variant.caption } : {})}
          isSelf={reply.isSelf}
          onTapOpen={variant.onTapOpen}
          onRetry={variant.onRetry}
        />
      );
    } else if (variant?.kind === 'doc') {
      bodyNode = (
        <DocAttachmentCard
          state={variant.state}
          filename={variant.filename}
          mimeType={variant.mimeType}
          byteSize={variant.byteSize}
          {...(variant.pageCount !== undefined ? { pageCount: variant.pageCount } : {})}
          isSelf={reply.isSelf}
          onTapPreview={variant.onTapPreview}
          onRetry={variant.onRetry}
        />
      );
    } else if (variant?.kind === 'poll') {
      bodyNode = (
        <PollView
          poll={variant.poll}
          counts={variant.counts}
          {...(variant.myVote !== undefined ? { myVote: variant.myVote } : {})}
          {...(variant.myVotes !== undefined ? { myVotes: variant.myVotes } : {})}
          total={variant.total}
          onVote={variant.onVote}
          isSelf={reply.isSelf}
        />
      );
    } else if (variant?.kind === 'location') {
      bodyNode = (
        <LocationAttachmentCard
          lat={variant.lat}
          lng={variant.lng}
          {...(variant.label !== undefined ? { label: variant.label } : {})}
          isSelf={reply.isSelf}
          {...(variant.onOpen !== undefined ? { onOpen: variant.onOpen } : {})}
        />
      );
    } else if (variant?.kind === 'game') {
      bodyNode = (
        <GameMessageCard
          gameKind={variant.gameKind}
          phase={variant.phase}
          isSelf={reply.isSelf}
        />
      );
    } else {
      bodyNode = <MessageBubble message={message} inThread {...bubbleActionProps} />;
      isTextBubble = true;
    }

    return (
      <View className="flex-row items-start gap-2 px-4 py-1" testID={testID}>
        {showHeader && !reply.isSelf && (
          <View className="w-8">
            <Avatar
              size="small"
              {...(reply.authorImageUri !== null ? { imageUri: reply.authorImageUri } : {})}
              initials={reply.authorName}
            />
          </View>
        )}
        {grouped && !reply.isSelf && <View className="w-10" />}
        <View className="min-w-0 flex-1 gap-0.5">
          {showHeader && !reply.isSelf && (
            <View className="flex-row items-baseline gap-2">
              <Text
                className="flex-shrink font-sans text-[14px] font-semibold text-light-text-primary dark:text-[#F5F6F8]"
                numberOfLines={1}
              >
                {reply.authorName}
              </Text>
              <Text className="font-sans text-[11px] font-medium text-light-text-tertiary dark:text-[#6B7280]">
                {formatHourMinute(reply.timestamp)}
              </Text>
            </View>
          )}
          {isTextBubble ? (
            bodyNode
          ) : (
            <View className={`flex flex-row ${reply.isSelf ? 'justify-end' : 'justify-start'}`}>
              {bodyNode}
            </View>
          )}
          {showFailedHint && (
            <Pressable
              onPress={() => onRetry?.(reply)}
              accessibilityRole="button"
              accessibilityLabel={threadsStrings.ping_threads_send_failed_hint}
              className="active:opacity-70"
            >
              <Text className="font-sans text-[12px] font-medium text-[#FF5353] pt-0.5">
                {threadsStrings.ping_threads_send_failed_hint}
              </Text>
            </Pressable>
          )}
        </View>
      </View>
    );
  },
);

ThreadMessageRow.displayName = 'ThreadMessageRow';

function formatHourMinute(timestamp: number): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
